/** PROTOTYPE live welding breakdown -- presentational. Price or Cost (COGS) view from the engine. */
import React, { useState } from 'react';
import { CRC } from './moduleInputsData';
import { Label, Segmented } from './protoAtoms';
import { Spread, machineSummary, money } from './spreadSplitData';
import { WeldMode, weldingMonths } from './spreadPricingApi';
import { SpreadPriceState } from './useSpreadPrice';
import { machineLines } from './weldingMachines';
import { MachineMixTable } from './MachineMixTable';

const MANUAL_KEY = 'manual';

const card: React.CSSProperties = { background: '#fff', border: `1px solid ${CRC.border}`, borderTop: `3px solid ${CRC.blueprintBlue}`, borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: 20 };
const num = (v: unknown): number => Number((v as number) ?? 0);

const Row: React.FC<{ label: string; value: string; note?: string; strong?: boolean }> = ({ label, value, note, strong }) => (
  <div style={{ padding: '5px 0', borderBottom: `1px solid ${CRC.rowDivider}` }}>
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 12, color: strong ? CRC.blueprintBlue : CRC.textGrey, fontWeight: strong ? 700 : 400 }}>{label}</span>
      <span style={{ fontFamily: 'Funnel Sans', fontSize: 13, fontWeight: 600, color: strong ? CRC.readableRed : CRC.blueprintBlue }}>{value}</span>
    </div>
    {note && <div style={{ fontSize: 10.5, color: CRC.heritageGrey, marginTop: 1 }}>{note}</div>}
  </div>
);

interface Props { spread: Spread; state: SpreadPriceState; mode: WeldMode; }
interface CostLine { label: string; cost: number; note?: string; }

export const SpreadWeldingLive: React.FC<Props> = ({ spread, state, mode }) => {
  const [view, setView] = useState<'price' | 'cost'>('price');
  const welding = state.status === 'done' ? state.result.welding : null;
  const d = welding ? welding.details : {};

  const machines = machineLines(d);
  // Manual hardware is priced off its own rates, so it gets its own line rather
  // than being blended into one hardware figure.
  const manual = machines.find((m) => m.key === MANUAL_KEY);
  const manualHardware = manual?.hardwarePrice ?? 0;

  const priceRows = () => {
    const oc = (d.otherCosts ?? {}) as Record<string, unknown>;
    const internetCloud = num(oc.internet) + num(oc.cloud);
    const services = num(oc.remoteSupport) + num(oc.training) + num(oc.provisioning) + num(oc.onFieldSupport);
    const hardware = num(d.hardwareCost);
    return (
      <>
        {manualHardware > 0 ? (
          <>
            <Row label="Hardware (automated)" value={money(hardware - manualHardware)}
              note={`${machines.filter((m) => m.key !== MANUAL_KEY).reduce((n, m) => n + m.units, 0)} machines`} />
            <Row label="Hardware (Manual)" value={money(manualHardware)}
              note={`${manual?.units} machines at ${money(manual?.unitCost ?? 0)} each`} />
          </>
        ) : (
          <Row label="Hardware" value={money(hardware)} />
        )}
        <Row label="Software" value={money(num(d.softwareCost))} />
        <Row label="Mobilization" value={money(num(d.mobilizationCost))} />
        <Row label="Internet & cloud connectivity" value={money(internetCloud)} />
        {services > 0 && <Row label="Services (support / training / etc.)" value={money(services)} />}
        {num(d.discountPercentage) > 0 && <Row label="Duration discount" value={`${num(d.discountPercentage)}%`} />}
        <Row label="Welding price (total)" value={money(welding ? welding.total : 0)} strong />
      </>
    );
  };

  const costRows = () => {
    const lines = (d.costLines ?? []) as CostLine[];
    return (
      <>
        {lines.map((l, i) => <Row key={i} label={l.label} value={money(num(l.cost))} note={l.note} />)}
        <Row label="Welding cost (COGS)" value={money(welding ? welding.cost : 0)} strong />
      </>
    );
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontFamily: 'Funnel Sans', fontSize: 15, fontWeight: 600, color: CRC.blueprintBlue }}>Welding breakdown</div>
          <div style={{ fontSize: 11.5, color: CRC.heritageGrey, marginTop: 2 }}>{machineSummary(spread)} · {weldingMonths(spread)} mo · {mode === 'offline' ? 'offline (no cloud)' : 'connected'}</div>
        </div>
        {state.status === 'done' && (
          <div><Label>View</Label><div style={{ marginTop: 4 }}>
            <Segmented value={view} onChange={(v) => setView(v as 'price' | 'cost')} options={[{ value: 'price', label: 'Price' }, { value: 'cost', label: 'Cost' }]} />
          </div></div>
        )}
      </div>

      <div style={{ marginTop: 14 }}>
        {state.status !== 'done' && state.status !== 'error' && <div style={{ fontSize: 13, color: CRC.heritageGrey }}>Calculating from the pricing engine…</div>}
        {state.status === 'error' && (
          <div style={{ fontSize: 12.5, color: CRC.readableRed, background: 'rgba(196,0,31,0.06)', borderRadius: 8, padding: '10px 12px' }}>
            Could not reach the pricing engine: {state.error}. (Are you signed in and is the backend reachable?)
          </div>
        )}
        {state.status === 'done' && (view === 'price' ? priceRows() : costRows())}
      </div>

      {state.status === 'done' && machines.length > 0 && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${CRC.border}` }}>
          <div style={{ fontFamily: 'Funnel Sans', fontSize: 13.5, fontWeight: 600, color: CRC.blueprintBlue, marginBottom: 2 }}>
            By machine
          </div>
          <div style={{ fontSize: 11, color: CRC.heritageGrey, marginBottom: 10 }}>
            What each machine type contributes to this spread's welding price and cost.
          </div>
          <MachineMixTable rows={machines} />
        </div>
      )}
    </div>
  );
};
